import {FC, useState} from 'react';

import {Box, Tab} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {DisabledExpressSelectorOptionIcon, ExpressSelectorOptionIconV2} from 'assets/icons/expressSelectorOptionIcon';
import {StandartDeliverySelectorOptionIcon} from 'assets/icons/standartDeliverySelectorOptionIcon';
import {StorePickupSelectorOptionIcon} from 'assets/icons/storePickupSelectorOptionIcon';
import useAnalytics, {ANALYTICS} from 'contexts/analytics-context';
import {useAppSelector} from 'hooks';
import {useSortedItemsByDelivery} from 'hooks/useSortedItemsByDelivery';
import {setAppModeAction} from 'store/actions/app-action';
import {ExpressTimeFrame} from 'types';
import {isTimeEligibleForExpress} from 'utils';

import {DeliveryOption} from './DeliveryOption';
import {DeliveryOptionSkeleton} from './DeliveryOptionSkeleton';
import {DeliveryExpressOption} from './ExpressDeliveryOption';
import {ExpressDisabledDrawer, ExpressUnavailableDrawer} from './ExpressUnavaialbleDrawer';
import {StyledTabs, TabMainContainer} from './styles';
import {IDeliverySelectorProps} from './types';
import {AppMode} from '../../constants';

export const DeliveryTypeSelector: FC<IDeliverySelectorProps> = ({isLoading, miniVersion, openAddressModal}) => {
  const dispatch = useDispatch();
  const appMode = useAppSelector((state) => state.app.appMode);
  const {basket} = useAppSelector((state) => state.basket);
  const {currentStore} = useAppSelector((state) => state.app);
  const {expressEligible, storePickupEligible, standardEligible} = useSortedItemsByDelivery(true);
  const analytics = useAnalytics();

  const [isExpressUnavailableDrawerOpen, setIsExpressUnavailableDrawerOpen] = useState<boolean>(false);
  const [isExpressDisabledLocationDrawerOpen, setIsExpressDisabledLocationDrawerOpen] = useState<boolean>(false);

  const isExpressDisabledForSelectedLocation = !currentStore?.expressStoreCodes?.length;

  const isExpressAfterCutoff = !isTimeEligibleForExpress(
    basket?.expressStoreInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame
  );

  const isExpressOptionDisabled =
    isExpressAfterCutoff || isExpressDisabledForSelectedLocation || !expressEligible?.length;

  const basketItemsInTotal = (basket?.basketItems?.length || 0) + (basket?.unavailableItems?.length || 0);

  const {t} = useTranslation();

  const setBasketMode = (mode: AppMode) => dispatch(setAppModeAction(mode));

  const onExpressDisabledClick = () => {
    if (isExpressDisabledForSelectedLocation) {
      setIsExpressDisabledLocationDrawerOpen(true);
    } else if (isExpressAfterCutoff) {
      setIsExpressUnavailableDrawerOpen(true);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="space-between" paddingX={1} paddingTop={0.25} paddingBottom={0.75}>
        <DeliveryOptionSkeleton />
        <DeliveryOptionSkeleton />
        <DeliveryOptionSkeleton />
      </Box>
    );
  }

  const onChangeMode: (event: React.SyntheticEvent, value: AppMode) => void = (_, value) => {
    if (value === AppMode.express) {
      isExpressOptionDisabled ? onExpressDisabledClick() : setBasketMode(value);
    } else {
      setBasketMode(value);
    }

    analytics.trackEvent(ANALYTICS.BASKET_MODE_SWITCHED, {
      mode: value,
      expressEligibleCount: expressEligible.length,
      storePickupEligibleCount: storePickupEligible.length,
      standardEligibleCount: standardEligible.length,
      expressStoreCodes: String(currentStore?.expressStoreCodes),
      regularStoreCodes: String(currentStore?.storeCodes),
      zip: String(currentStore?.zipcode),
      sub: String(currentStore?.subdistrictEn),
      storeId: basket?.storeCode || '',
      basketSize: basket?.basketItems?.length || 0
    });
  };

  if (miniVersion) {
    return (
      <TabMainContainer>
        <StyledTabs value={appMode} onChange={onChangeMode} variant="fullWidth">
          <Tab
            disabled={isExpressOptionDisabled}
            value={AppMode.express}
            data-test-id={`${AppMode.express}_delivery_option_shrank`}
            label={t('unifiedBasket.label.express')}
            icon={
              isExpressOptionDisabled ? (
                <DisabledExpressSelectorOptionIcon height="16px" width="16px" />
              ) : (
                <ExpressSelectorOptionIconV2 height="16px" width="16px" />
              )
            }
            iconPosition="start"
          />
          <Tab
            value={AppMode.regular}
            label={t('unifiedBasket.label.scheduled')}
            data-test-id={`${AppMode.regular}_delivery_option_shrank`}
            icon={<StandartDeliverySelectorOptionIcon height="16px" width="16px" />}
            iconPosition="start"
          />
          <Tab
            value={AppMode.storePickup}
            label={t('unifiedBasket.label.storePickup')}
            data-test-id={`${AppMode.storePickup}_delivery_option_shrank`}
            icon={<StorePickupSelectorOptionIcon height="16px" width="16px" />}
            iconPosition="start"
          />
        </StyledTabs>
      </TabMainContainer>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      paddingX={1}
      paddingTop={0.25}
      paddingBottom={0.75}
      borderRadius={2}
      margin={1}
      bgcolor="#F4F4F5"
      data-test-id={`delivery_options_container`}
    >
      <DeliveryExpressOption
        isSelected={appMode === AppMode.express}
        name={t('unifiedBasket.label.express')}
        Icon={() => (isExpressOptionDisabled ? <DisabledExpressSelectorOptionIcon /> : <ExpressSelectorOptionIconV2 />)}
        totalItems={basketItemsInTotal}
        eligibleItems={expressEligible?.length}
        onClick={(event) => onChangeMode(event, AppMode.express)}
        isDisabled={isExpressOptionDisabled}
        type={AppMode.express}
        isOutOfOrderingTime={isExpressAfterCutoff}
        isLocationNotEligibleForExpress={isExpressDisabledForSelectedLocation}
      />
      <DeliveryOption
        name={t('unifiedBasket.title.scheduled')}
        Icon={StandartDeliverySelectorOptionIcon}
        totalItems={basketItemsInTotal}
        isSelected={appMode === AppMode.regular}
        eligibleItems={standardEligible.length}
        onClick={(event) => onChangeMode(event, AppMode.regular)}
        type={AppMode.regular}
      />
      <DeliveryOption
        name={t('unifiedBasket.title.storePickup')}
        Icon={StorePickupSelectorOptionIcon}
        totalItems={basketItemsInTotal}
        isSelected={appMode === AppMode.storePickup}
        eligibleItems={storePickupEligible.length}
        onClick={(event) => onChangeMode(event, AppMode.storePickup)}
        type={AppMode.regular}
      />
      <ExpressUnavailableDrawer
        open={isExpressUnavailableDrawerOpen}
        onClose={() => setIsExpressUnavailableDrawerOpen(false)}
      />
      <ExpressDisabledDrawer
        open={isExpressDisabledLocationDrawerOpen}
        onClose={() => setIsExpressDisabledLocationDrawerOpen(false)}
        openAddressModal={openAddressModal}
      />
    </Box>
  );
};
